import { ViewTransition, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';

import { useComponents, useIncidents, useQueue } from '@/api/queries';
import { CountPill } from '@/components/badges';
import { ExternalLink } from '@/components/icons';
import { NavIndicator, TabLabel } from '@/components/NavIndicator';
import { Timestamp } from '@/components/Timestamp';
import { SCOPE_LABEL } from '@/domain/health';

import { NotFoundPage } from './errors';
import { scopePath, useScopeParam } from './scope';

export function ScopeLayout() {
  const scope = useScopeParam();
  if (!scope) return <NotFoundPage />;
  return <ScopeFrame scope={scope} />;
}

function ScopeFrame({ scope }: { scope: NonNullable<ReturnType<typeof useScopeParam>> }) {
  const components = useComponents(scope);
  const queue = useQueue(scope);
  const incidents = useIncidents(scope);
  const { pathname } = useLocation();

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-page font-medium">{SCOPE_LABEL[scope]}</h1>
          {components.data ? (
            <p className="text-body text-ink-3 mt-0.5">
              As of <Timestamp iso={components.data.as_of} mode="clock" withSeconds />
            </p>
          ) : null}
        </div>
        {incidents.data?.page_url ? (
          <a
            href={incidents.data.page_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-body"
          >
            Public status page <ExternalLink size={14} />
          </a>
        ) : null}
      </div>

      <nav
        aria-label="Sections"
        className="relative border-b border-line flex gap-1 overflow-x-auto overflow-y-hidden scrollbar-thin"
      >
        <SectionLink to={scopePath(scope)} end label="Components" />
        <SectionLink
          to={scopePath(scope, 'queue')}
          label="Approvals"
          badge={<CountPill count={queue.data?.length ?? 0} tone="attention" />}
        />
        <SectionLink
          to={scopePath(scope, 'incidents')}
          label="Incidents"
          badge={<CountPill count={incidents.data?.incidents.length ?? 0} />}
        />
        <SectionLink to={scopePath(scope, 'config')} label="Configuration" />
        <NavIndicator activeKey={pathname} />
      </nav>

      {/* Switching sections inside a scope: only the section below the tabs moves. */}
      <ViewTransition key={pathname} enter="page-in" exit="page-out" default="none">
        <div>
          <Outlet />
        </div>
      </ViewTransition>
    </div>
  );
}

function SectionLink({
  to,
  end,
  label,
  badge,
}: {
  to: string;
  end?: boolean;
  label: string;
  badge?: ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end ?? false}
      className={({ isActive }) =>
        [
          // As in the scope strip: the first label sits on the content edge, under the page title.
          'inline-flex items-center gap-2 h-9 px-3 first:pl-0 text-body whitespace-nowrap no-underline hover:no-underline transition-tone focus-visible:-outline-offset-2',
          isActive ? 'text-brand font-medium' : 'text-ink-2 hover:text-ink',
        ].join(' ')
      }
    >
      <TabLabel>{label}</TabLabel>
      {badge}
    </NavLink>
  );
}
